/**
 * 319: the "Where it was taken" line under a photo, and its Add location /
 * Move button. Used by the Gallery viewer and by the poster's own photo in
 * the feed viewer. What it offers comes from photo_place_state — the same rule
 * place_photo applies — so the screen never offers a button the server would
 * refuse, and never re-decides who may.
 *
 * 322: with `showMap` (the Gallery viewer's details) a small map shows the
 * point above the line, from the same answer - nothing more is read.
 *
 * 324: Suggest location asks One where it was taken and lists up to five
 * places; choosing one is placePhoto, so the person who chose it placed it.
 * Offered where Add / Move is, except on a point a person placed (David:
 * "unless it was user added") - photo_location_hints refuses those too.
 */
import { lazy, Suspense, useEffect, useState } from 'react'
import {
  loadPlaceState,
  placePhoto,
  suggestionSureness,
  suggestLocations,
  type LocationSuggestion,
  type PlaceState,
} from '../lib/galleryMap'
import { GALLERY_MAP } from '../lib/hubCopy'
import { useHubLang } from '../lib/i18n'
import { PlaceIcon } from './icons'

const LocationPicker = lazy(() => import('./LocationPicker'))
const PhotoMiniMap = lazy(() => import('./PhotoMiniMap'))

export function PlaceOnMap({
  path,
  onlyMine = false,
  onPlaced,
  className = 'placeonmap',
  showMap = false,
}: {
  path: string
  /** The feed viewer: say nothing about a colleague's photo there. */
  onlyMine?: boolean
  onPlaced?: (path: string) => void
  className?: string
  /** 322: a small map with the pin, above the line (the Gallery viewer's details). */
  showMap?: boolean
}) {
  const lang = useHubLang()
  const t = GALLERY_MAP[lang]
  const [state, setState] = useState<PlaceState | null | undefined>(undefined)
  const [picking, setPicking] = useState(false)
  const [done, setDone] = useState(false)
  const [rev, setRev] = useState(0)
  /* 324: null = not asked; 'loading'; the list (maybe empty); or the failure's words. */
  const [offers, setOffers] = useState<LocationSuggestion[] | 'loading' | { failed: string } | null>(null)
  const [savingAt, setSavingAt] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<string | null>(null)

  useEffect(() => {
    setDone(false)
    setOffers(null)
    setSavedAt(null)
  }, [path])
  useEffect(() => {
    let live = true
    loadPlaceState([path])
      .then((m) => live && setState(m.get(path) ?? null))
      .catch(() => live && setState(null))
    return () => {
      live = false
    }
  }, [path, rev])

  // Not ours to show: a photo the caller cannot see, or a colleague's in the feed.
  if (!state || (onlyMine && !state.isPoster)) return null
  // Nothing to say and nothing to do (e.g. a contest-only photo with no location).
  if (!state.hasLocation && !state.mayPlace && !(state.optedOut && !state.isPoster)) return null

  const status = state.hasLocation
    ? state.source === 'picked'
      ? t.placedBy(state.locatedByName)
      : t.placedCamera
    : state.optedOut && !state.isPoster
      ? t.optedOut
      : t.notPlaced

  const point = state.hasLocation && state.lat !== null && state.lng !== null ? { lat: state.lat, lng: state.lng } : null
  /* 324: not on a point a person placed - that one is moved by hand. */
  const maySuggest = state.mayPlace && state.source !== 'picked'

  async function askOne() {
    setOffers('loading')
    try {
      setOffers(await suggestLocations(path, lang))
    } catch (e) {
      setOffers({ failed: e instanceof Error && e.message ? e.message : '' })
    }
  }

  async function choose(s: LocationSuggestion) {
    setSavingAt(s.name)
    try {
      await placePhoto(path, s.lat, s.lng)
      setOffers(null)
      setSavedAt(s.name)
      setDone(true)
      setRev((r) => r + 1)
      onPlaced?.(path)
    } catch (e) {
      setOffers({ failed: e instanceof Error && e.message ? e.message : '' })
    } finally {
      setSavingAt(null)
    }
  }

  return (
    <>
      {showMap && point && (
        <Suspense fallback={<div className="minimap minimap--loading" aria-hidden="true" />}>
          <PhotoMiniMap key={`${point.lat},${point.lng}`} lat={point.lat} lng={point.lng} t={t} />
        </Suspense>
      )}
      <div className={className}>
        <PlaceIcon size={14} />{' '}
        <span role="status">{done ? (savedAt ? t.suggestSaved(savedAt) : t.placedSaved) : status}</span>
        {state.mayPlace && (
          <>
            {' '}
            <button type="button" className="btn btn--link btn--sm" onClick={() => setPicking(true)}>
              {state.hasLocation ? t.move : t.add}
            </button>
          </>
        )}
        {maySuggest && offers === null && (
          <>
            {' '}
            <button type="button" className="btn btn--link btn--sm" onClick={() => void askOne()}>
              {state.hasLocation ? t.suggestAgain : t.suggest}
            </button>
          </>
        )}
        {offers !== null && (
          <div className="placesuggest" aria-live="polite">
            {offers === 'loading' ? (
              <p className="placesuggest__note">{t.suggesting}</p>
            ) : !Array.isArray(offers) ? (
              <p className="placesuggest__note">
                {t.suggestFailed}
                {offers.failed ? ` ${offers.failed}` : ''}{' '}
                <button type="button" className="btn btn--link btn--sm" onClick={() => setOffers(null)}>
                  {t.suggestDismiss}
                </button>
              </p>
            ) : offers.length === 0 ? (
              <p className="placesuggest__note">
                {t.suggestNone}{' '}
                <button type="button" className="btn btn--link btn--sm" onClick={() => setOffers(null)}>
                  {t.suggestDismiss}
                </button>
              </p>
            ) : (
              <>
                <p className="placesuggest__note">{t.suggestTitle}</p>
                <ul className="placesuggest__list">
                  {offers.map((s) => (
                    <li key={`${s.name}-${s.lat}-${s.lng}`}>
                      <button
                        type="button"
                        className="placesuggest__pick"
                        disabled={savingAt !== null}
                        aria-label={t.suggestChoose(s.name)}
                        onClick={() => void choose(s)}
                      >
                        <span className="placesuggest__name">
                          {s.name}
                          {s.known && <span className="placesuggest__badge">{t.suggestKnown}</span>}
                        </span>
                        {s.place && <span className="placesuggest__where">{s.place}</span>}
                        <span className="placesuggest__why">
                          {savingAt === s.name ? t.pickSaving : t.suggestSure[suggestionSureness(s.confidence)]}
                          {s.why ? ` · ${s.why}` : ''}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                <button type="button" className="btn btn--link btn--sm" onClick={() => setOffers(null)}>
                  {t.suggestDismiss}
                </button>
              </>
            )}
          </div>
        )}
        {picking && (
          <Suspense fallback={null}>
            <LocationPicker
              initial={state.lat !== null && state.lng !== null ? { lat: state.lat, lng: state.lng } : null}
              onSave={async (p) => {
                await placePhoto(path, p.lat, p.lng)
                setDone(true)
                setRev((r) => r + 1)
                onPlaced?.(path)
              }}
              onClose={() => setPicking(false)}
              t={t}
              lang={lang}
            />
          </Suspense>
        )}
      </div>
    </>
  )
}
